import { Merchant, MerchantContactInformation, MerchantInvoiceInformation } from './Merchant';

export type SalesSegment = 'B2C' | 'B2B';

export const salesSegments: { value: SalesSegment; label: string; description: string }[] = [
    { value: 'B2C', label: 'B2C', description: 'Sells to consumers' },
    { value: 'B2B', label: 'B2B', description: 'Sells to companies' },
];

/** What the "Onboard store" modal collects. */
export interface OnboardStoreInput {
    salesSegment: SalesSegment;
    countryCode: string;
    /** HTTPS URL to an SVG or PNG. Empty when no logo is set. */
    logoUrl: string;
    /** Name shown to shoppers. Falls back to the organization name when left blank in the modal. */
    presentationName: string;
    /** The URL of the store, one of the merchant's allowed URLs (see Merchant.allowedUrls). The API rejects any other. */
    websiteUrl: string;
    /** Email printed on the invoices of the store. Prefilled from the merchant in the modal, so it is usually the merchant's. */
    email: string;
    /**
     * Phone number printed on the invoices of the store. Prefilled from the merchant in the modal, like the email, but
     * optional: an empty string gives the store no phone number. It is sent as is, since the API falls back to the
     * merchant's phone number only when the field is left out.
     */
    phone: string;
    /**
     * IBAN of the settlement account, one of the merchant's verified bank accounts
     * (see MerchantBankAccount). The API rejects any other account.
     */
    iban: string;
    /**
     * Organization the store is onboarded for. Only sent by internal users, who onboard on behalf
     * of the selected organization. For merchant users the API uses their own organization.
     */
    groupCode?: string;
}

export interface OnboardingRequestMerchant {
    name: string;
    organizationNumber: string;
    vatRegistrationCode: string;
    countryCode: string;
    contactInformation: MerchantContactInformation;
    invoiceInformation: MerchantInvoiceInformation;
}

/**
 * Onboarding template for stores created from Merchant Hub when the merchant has no template of its own. Must match a
 * template category configured for the organization (or the root organization).
 */
export const STORE_TEMPLATE = 'StandardCheckout';

/**
 * The onboarding template a store of the merchant is created from: the template of the merchant (from its signup key)
 * when it has one, else STORE_TEMPLATE. The API refuses any other template for a merchant that has one.
 */
export const getStoreTemplate = (merchant: Merchant): string =>
    merchant.template?.trim() || STORE_TEMPLATE;

/** The API requires a SalesCategory on the store. Every store onboarded from Merchant Hub is Retail. */
export const STORE_SALES_CATEGORY = 'Retail';

/** Settlement account of the store: the IBAN of one of the merchant's verified bank accounts. */
export interface OnboardingRequestRemittance {
    account: {
        type: 'Iban';
        number: string;
    };
}

/**
 * Contact information of the store. Only the URL, email and phone are sent; the API falls back to the merchant
 * for the rest (the contact name and address).
 */
export interface OnboardingRequestStoreContactInformation {
    websiteUrl: string;
    email: string;
    phone: string;
}

/**
 * Store part of the onboarding body. Organization number, VAT registration code and the contact name and
 * address are left out on purpose: the API falls back to the merchant for them. Of the
 * remittance settings only the account is sent; the API ignores anything else from Merchant Hub.
 */
export interface OnboardingRequestStore {
    /** See getStoreTemplate. */
    template: string;
    salesSegment: SalesSegment;
    countryCode: string;
    salesCategory: string;
    /** Omitted when no logo is set. */
    logoUrl?: string;
    presentationName: string;
    legalName: string;
    contactInformation: OnboardingRequestStoreContactInformation;
    remittance: OnboardingRequestRemittance;
}

/** Body for POST onboarding. */
export interface OnboardingRequest {
    /** Selected organization name. Required by the API for internal users, omitted otherwise. */
    groupCode?: string;
    merchant: OnboardingRequestMerchant;
    stores: OnboardingRequestStore[];
}

export interface OnboardingStoreResult {
    id?: number | null;
    salesSegment: string;
    countryCode: string;
    /** HTTP status per store: 200 created, 202 partially created, 409 already exists, 500 failed. */
    status: number | string;
    errors?: string[];
}

/**
 * Response from POST onboarding. The response also carries a `data` object with authentication
 * details for API onboardings. It is deliberately left out of this type so credentials never
 * reach the UI.
 */
export interface OnboardingResponse {
    results: OnboardingStoreResult[];
}

export interface OnboardedStore {
    id?: number | null;
    salesSegment: string;
    countryCode: string;
    presentationName: string;
    isPartiallyCreated: boolean;
}
